import { Icon, IconButton, Text, Title } from "@assetwatch/ui";
import type { TFunction } from "i18next";
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EnterpriseApiError,
  type ReliChatMessage,
  type ReliClarificationSelection,
  sendReliMessage,
} from "../api/enterpriseClient";
import type {
  AskReliAttachedContext,
  AskReliConversationRecord,
  AskReliCustomerSummary,
  AskReliDisplayMessage,
  AskReliFacility,
  AskReliSuggestedQuestion,
  ReliVisualization,
} from "./AskReli.types";
import { reliAssistantPanelStyles as styles } from "./ReliAssistantPanel.styles";

const WELCOME_MESSAGE_ID = "welcome";
const MAX_RELI_HISTORY_MESSAGES = 20;

// Prompts sent to the Ask Reli backend are kept in English on purpose: the
// backend SQL prompt and its exact-match known-query helper are English-tuned,
// so localized prompt text would degrade SQL quality / miss the canonical path.
// These strings are never displayed — only the localized `title`/chip copy is.
const TEMPLATE_PROMPTS = {
  focusToday: "What should I focus on today?",
  biggestWin: "What was my biggest win in the last 30 days?",
  missed: "What might I have missed?",
  enterpriseValue: "How can I get more value from Enterprise?",
} as const;

const ATTACHED_CONTEXT_LABEL = "Attached context";

const numberFormatter = new Intl.NumberFormat("en-US");

function doesTextSelectOption(
  text: string,
  option: { id: string; label: string },
) {
  const normalizedText = text.trim().toLowerCase();
  const normalizedOptionId = option.id.toLowerCase();
  const normalizedOptionLabel = option.label.toLowerCase();

  return (
    normalizedOptionId === normalizedText ||
    normalizedOptionLabel === normalizedText ||
    normalizedText.includes(normalizedOptionLabel) ||
    (normalizedText.length >= 4 &&
      normalizedOptionLabel.includes(normalizedText))
  );
}

function doesTextExactlySelectOption(
  text: string,
  option: { id: string; label: string },
) {
  const normalizedText = text.trim().toLowerCase();

  return (
    option.id.toLowerCase() === normalizedText ||
    option.label.toLowerCase() === normalizedText
  );
}

function getClarificationSelection(
  text: string,
  clarification:
    | NonNullable<AskReliDisplayMessage["clarification"]>
    | undefined,
): ReliClarificationSelection | undefined {
  if (!clarification) return undefined;

  const base = {
    clarificationId: clarification.id,
    question: clarification.question,
  };

  if (clarification.mode === "free_text") {
    return { ...base, mode: "free_text", response: text };
  }

  const selectedOptions = clarification.options.filter((option) =>
    doesTextSelectOption(text, option),
  );
  if (clarification.mode === "multi_select") {
    return selectedOptions.length > 0
      ? { ...base, mode: "multi_select", options: selectedOptions }
      : undefined;
  }

  const option =
    clarification.options.find((candidate) =>
      doesTextExactlySelectOption(text, candidate),
    ) ?? selectedOptions[0];
  return option ? { ...base, mode: "select", option } : undefined;
}

function formatCount(value: number) {
  return numberFormatter.format(value);
}

function getDayPeriod() {
  const hour = new Date().getHours();

  if (hour < 12) {
    return "morning";
  }

  if (hour < 18) {
    return "afternoon";
  }

  return "evening";
}

function formatHeaderDate() {
  return new Date()
    .toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      weekday: "short",
    })
    .toUpperCase();
}

function getFacilityRiskScore(facility: AskReliFacility) {
  return (
    facility.openAlertCostOfInaction +
    facility.maintenanceRecommendedMachineCount * 10_000 +
    facility.watchingMachineCount * 5_000
  );
}

function buildFacilityContexts(
  t: TFunction,
  facilities: AskReliFacility[] | undefined,
) {
  return [...(facilities ?? [])]
    .sort((a, b) => getFacilityRiskScore(b) - getFacilityRiskScore(a))
    .slice(0, 8)
    .map((facility) => ({
      id: `facility-${facility.facilityId}`,
      label: facility.facilityName,
      description: t("enterprise.reliability.assistant.requiredWatches", {
        required: formatCount(facility.maintenanceRecommendedMachineCount),
        watches: formatCount(facility.watchingMachineCount),
      }),
    }));
}

function formatMessageWithContexts(
  text: string,
  contexts: AskReliAttachedContext[],
) {
  if (contexts.length === 0) {
    return text;
  }

  const contextText = contexts
    .map((context) => `${context.label} (${context.description})`)
    .join("; ");

  return `${text}\n\n${ATTACHED_CONTEXT_LABEL}: ${contextText}`;
}

function getConversationTitle(messages: AskReliDisplayMessage[]) {
  const firstUserMessage = messages.find((message) => message.role === "user");
  return firstUserMessage?.content.trim() || null;
}

function formatConversationPreview(title: string) {
  return title.length > 42 ? `${title.slice(0, 39).trim()}...` : title;
}

function formatConversationTimestamp(t: TFunction, updatedAt: number) {
  const elapsedSeconds = Math.max(
    0,
    Math.floor((Date.now() - updatedAt) / 1000),
  );

  if (elapsedSeconds < 60) {
    return t("enterprise.reliability.assistant.justNow");
  }

  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) {
    return t("enterprise.reliability.assistant.minAgo", {
      count: elapsedMinutes,
    });
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return t("enterprise.reliability.assistant.hrAgo", { count: elapsedHours });
  }

  const elapsedDays = Math.floor(elapsedHours / 24);
  return t("enterprise.reliability.assistant.dayAgo", { count: elapsedDays });
}

function AskReliSparklesMark({ className = "" }: { className?: string }) {
  return (
    <div
      className={[styles.askReliMark, className || "text-primary"]
        .filter(Boolean)
        .join(" ")}
      aria-hidden="true"
    >
      <Icon name="sparkles" size="md" tone="inherit" aria-hidden />
    </div>
  );
}

function withOccurrenceKeys(values: string[]) {
  const occurrences = new Map<string, number>();

  return values.map((value) => {
    const occurrence = occurrences.get(value) ?? 0;
    occurrences.set(value, occurrence + 1);

    return { key: `${value}-${occurrence}`, value };
  });
}

function localizeWelcomeMessage(
  messages: AskReliDisplayMessage[],
  welcomeMessageContent: string,
) {
  return messages.map((message) =>
    message.id === WELCOME_MESSAGE_ID
      ? { ...message, content: welcomeMessageContent }
      : message,
  );
}

function renderInlineMarkdown(text: string): ReactNode[] {
  const segments = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean);
  return withOccurrenceKeys(segments).map(({ key, value: segment }) => {
    const boldMatch = /^\*\*([^*]+)\*\*$/.exec(segment);
    if (boldMatch) {
      return <strong key={`bold-${key}`}>{boldMatch[1]}</strong>;
    }

    const italicMatch = /^\*([^*]+)\*$/.exec(segment);
    if (italicMatch) {
      return <em key={`italic-${key}`}>{italicMatch[1]}</em>;
    }

    return <Fragment key={`text-${key}`}>{segment}</Fragment>;
  });
}

function MarkdownLiteContent({ content }: { content: string }) {
  const blocks = content
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  return (
    <div className="flex flex-col gap-(--spacing-6)">
      {withOccurrenceKeys(blocks).map(({ key: blockKey, value: block }) => {
        const lines = block
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean);
        const headingMatch = /^(#{1,3})\s+(.+)$/.exec(block);
        const isNumberedList = lines.every((line) => /^\d+\.\s+/.test(line));
        const isBulletList = lines.every((line) => /^[-*]\s+/.test(line));

        if (headingMatch) {
          return (
            <Title
              key={`block-${blockKey}`}
              size={headingMatch[1].length === 1 ? "md" : "sm"}
              as={headingMatch[1].length === 1 ? "h3" : "h4"}
            >
              {renderInlineMarkdown(headingMatch[2])}
            </Title>
          );
        }

        if (isNumberedList) {
          return (
            <ol
              key={`block-${blockKey}`}
              className="m-0 flex list-decimal flex-col gap-(--spacing-4) pl-(--spacing-16)"
            >
              {withOccurrenceKeys(lines).map(({ key, value: line }) => (
                <li key={`line-${key}`}>
                  <Text size="sm" as="span">
                    {renderInlineMarkdown(line.replace(/^\d+\.\s+/, ""))}
                  </Text>
                </li>
              ))}
            </ol>
          );
        }

        if (isBulletList) {
          return (
            <ul
              key={`block-${blockKey}`}
              className="m-0 flex list-disc flex-col gap-(--spacing-4) pl-(--spacing-16)"
            >
              {withOccurrenceKeys(lines).map(({ key, value: line }) => (
                <li key={`line-${key}`}>
                  <Text size="sm" as="span">
                    {renderInlineMarkdown(line.replace(/^[-*]\s+/, ""))}
                  </Text>
                </li>
              ))}
            </ul>
          );
        }

        return (
          <Text key={`block-${blockKey}`} size="sm" as="div">
            {renderInlineMarkdown(block)}
          </Text>
        );
      })}
    </div>
  );
}

function VisualizationBarChart({
  visualization,
}: {
  visualization: ReliVisualization;
}) {
  const maxValue = Math.max(
    ...visualization.points.map((point) => point.value),
    1,
  );

  return (
    <div className={styles.chart}>
      <div className={styles.chartTitle}>
        <Text size="sm" as="div">
          {visualization.title}
        </Text>
      </div>
      <div className={styles.chartAxes}>
        <Text size="sm" tone="subtle">
          {visualization.yLabel}
        </Text>
      </div>
      <div className={styles.chartBars}>
        {visualization.points.map((point) => (
          <div
            className={styles.chartRow}
            key={`${point.label}-${point.value}`}
          >
            <div className={styles.chartLabel} title={point.label}>
              {point.label}
            </div>
            <div className={styles.chartBarWrap}>
              <div
                className={styles.chartBar}
                style={{
                  width: `${Math.max((point.value / maxValue) * 100, 6)}%`,
                }}
              />
            </div>
            <div className={styles.chartValue}>
              {point.value.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
      <Text size="sm" tone="subtle">
        {visualization.xLabel}
      </Text>
    </div>
  );
}

export function ReliAssistantPanel({
  customerId,
  chrome = "page",
  customerFacilities,
  customerSummary,
  isExpanded = false,
  userName,
  onClose,
  onToggleExpand,
}: {
  customerId: number;
  chrome?: "page" | "floating";
  customerFacilities?: AskReliFacility[];
  customerSummary?: AskReliCustomerSummary;
  contextLabel?: string;
  isExpanded?: boolean;
  userName?: string;
  onClose?: () => void;
  onToggleExpand?: () => void;
}) {
  const { t } = useTranslation();

  const WELCOME_MESSAGE: AskReliDisplayMessage = {
    id: WELCOME_MESSAGE_ID,
    role: "assistant",
    content: t("enterprise.reliability.assistant.welcomeMessage"),
  };

  const TEMPLATE_QUESTIONS = [
    {
      icon: "alert-exclamation",
      title: t("enterprise.reliability.assistant.predefinedTitleFocusToday"),
      prompt: TEMPLATE_PROMPTS.focusToday,
    },
    {
      icon: "cost-savings-dollar",
      title: t("enterprise.reliability.assistant.predefinedTitleBiggestWin"),
      prompt: TEMPLATE_PROMPTS.biggestWin,
    },
    {
      icon: "trend-up",
      title: t("enterprise.reliability.assistant.predefinedTitleMissed"),
      prompt: TEMPLATE_PROMPTS.missed,
    },
    {
      icon: "sparkles",
      title: t("enterprise.reliability.assistant.predefinedTitleValue"),
      prompt: TEMPLATE_PROMPTS.enterpriseValue,
    },
  ] as const satisfies readonly AskReliSuggestedQuestion[];

  const messageIdCounterRef = useRef(0);

  function createMessageId(prefix: string) {
    messageIdCounterRef.current += 1;
    return `${prefix}-${messageIdCounterRef.current}`;
  }

  function createConversationId() {
    return createMessageId("conversation");
  }

  const [messages, setMessages] = useState<AskReliDisplayMessage[]>([
    WELCOME_MESSAGE,
  ]);
  const [activeConversationId, setActiveConversationId] =
    useState(createConversationId);
  const [conversations, setConversations] = useState<
    AskReliConversationRecord[]
  >([]);
  const [input, setInput] = useState("");
  const [attachedContexts, setAttachedContexts] = useState<
    AskReliAttachedContext[]
  >([]);
  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [showMobileHistory, setShowMobileHistory] = useState(false);
  const [loadingConversationIds, setLoadingConversationIds] = useState<
    Set<string>
  >(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const activeConversationIdRef = useRef(activeConversationId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const activeIsLoading = loadingConversationIds.has(activeConversationId);
  const welcomeMessageContentRef = useRef(WELCOME_MESSAGE.content);

  useEffect(() => {
    welcomeMessageContentRef.current = WELCOME_MESSAGE.content;
    setMessages((currentMessages) =>
      localizeWelcomeMessage(currentMessages, WELCOME_MESSAGE.content),
    );
    setConversations((currentConversations) =>
      currentConversations.map((conversation) => ({
        ...conversation,
        messages: localizeWelcomeMessage(
          conversation.messages,
          WELCOME_MESSAGE.content,
        ),
      })),
    );
  }, [WELCOME_MESSAGE.content]);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    if (!activeIsLoading && messages.length === 1) {
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeIsLoading, messages.length]);

  useEffect(() => {
    if (isExpanded) {
      setShowMobileHistory(false);
    }
  }, [isExpanded]);

  function persistConversation(
    conversationId: string,
    nextMessages: AskReliDisplayMessage[],
    loading = false,
  ) {
    const localizedMessages = localizeWelcomeMessage(
      nextMessages,
      welcomeMessageContentRef.current,
    );
    const title = getConversationTitle(localizedMessages);

    setConversations((current) => {
      const otherConversations = current.filter(
        (conversation) => conversation.id !== conversationId,
      );

      if (!title) {
        return otherConversations;
      }

      return [
        {
          id: conversationId,
          title,
          messages: localizedMessages,
          updatedAt: Date.now(),
          isLoading: loading,
        },
        ...otherConversations,
      ].slice(0, 12);
    });
  }

  function setConversationLoading(conversationId: string, loading: boolean) {
    setLoadingConversationIds((current) => {
      const nextLoadingConversationIds = new Set(current);

      if (loading) {
        nextLoadingConversationIds.add(conversationId);
      } else {
        nextLoadingConversationIds.delete(conversationId);
      }

      return nextLoadingConversationIds;
    });
  }

  function setMessagesIfActive(
    conversationId: string,
    nextMessages: AskReliDisplayMessage[],
  ) {
    if (activeConversationIdRef.current === conversationId) {
      setMessages(
        localizeWelcomeMessage(nextMessages, welcomeMessageContentRef.current),
      );
    }
  }

  async function handleSend(messageText?: string) {
    const text = (messageText ?? input).trim();
    if (!text || activeIsLoading) return;

    setInput("");
    setError(null);
    setIsAttachOpen(false);
    setShowMobileHistory(false);

    let conversationId = activeConversationId;
    if (
      messages.length === 1 &&
      conversations.some((conversation) => conversation.id === conversationId)
    ) {
      conversationId = createConversationId();
      activeConversationIdRef.current = conversationId;
      setActiveConversationId(conversationId);
    }
    const messageContexts = [...attachedContexts];

    const userMessage: AskReliDisplayMessage = {
      id: createMessageId("user"),
      role: "user",
      content: text,
      contexts: messageContexts.map((context) => context.label),
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    persistConversation(conversationId, nextMessages, true);
    setConversationLoading(conversationId, true);
    setAttachedContexts([]);

    const history: ReliChatMessage[] = nextMessages
      .slice(0, -1)
      .filter((m) => m.id !== WELCOME_MESSAGE_ID)
      .slice(-MAX_RELI_HISTORY_MESSAGES)
      .map((m) => ({
        role: m.role,
        content: [
          m.content,
          ...(m.contexts && m.contexts.length > 0
            ? [`${ATTACHED_CONTEXT_LABEL}: ${m.contexts.join("; ")}`]
            : []),
        ].join("\n\n"),
        ...(m.role === "assistant"
          ? { clarification: m.clarification ?? null }
          : {}),
      }));
    const pendingClarification = [...messages]
      .reverse()
      .find((message) => message.role === "assistant")?.clarification;
    const clarificationSelection = getClarificationSelection(
      text.trim(),
      pendingClarification ?? undefined,
    );

    try {
      const result = await sendReliMessage(
        customerId,
        formatMessageWithContexts(text, messageContexts),
        history,
        clarificationSelection,
        pendingClarification ?? undefined,
      );
      const completedMessages = [
        ...nextMessages,
        {
          id: createMessageId("assistant"),
          role: "assistant",
          content: result.answer,
          rowCount: result.rowCount,
          sqlExpanded: false,
          visualization: result.visualization,
          clarification: result.clarification,
        },
      ] satisfies AskReliDisplayMessage[];
      setMessagesIfActive(conversationId, completedMessages);
      persistConversation(conversationId, completedMessages);
    } catch (err) {
      if (activeConversationIdRef.current === conversationId) {
        if (
          err instanceof EnterpriseApiError &&
          (err.status === 502 || err.status === 503 || err.status === 504)
        ) {
          setError(t("enterprise.reliability.assistant.errorTimeout"));
        } else {
          setError(t("enterprise.reliability.assistant.errorGeneric"));
        }
        setMessagesIfActive(conversationId, nextMessages);
        setAttachedContexts(messageContexts);
      }
      persistConversation(conversationId, nextMessages);
    } finally {
      setConversationLoading(conversationId, false);
      if (activeConversationIdRef.current === conversationId) {
        textareaRef.current?.focus();
      }
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  function toggleSql(index: number) {
    const nextMessages = messages.map((message, messageIndex) =>
      messageIndex === index
        ? { ...message, sqlExpanded: !message.sqlExpanded }
        : message,
    );
    setMessages(nextMessages);
    persistConversation(activeConversationId, nextMessages);
  }

  const isFloating = chrome === "floating";
  const hasActiveConversation = messages.length > 1 || activeIsLoading;
  const greetingName =
    userName?.trim() ||
    t("enterprise.reliability.assistant.greetingFallbackName");
  const actionRequiredCount =
    customerSummary?.maintenanceRecommendedMachineCount ?? 0;
  const watchCount = customerSummary?.watchingMachineCount ?? 0;
  const attachmentOptions = buildFacilityContexts(t, customerFacilities);
  const greetingKeyByPeriod = {
    morning: "enterprise.reliability.assistant.greetingMorning",
    afternoon: "enterprise.reliability.assistant.greetingAfternoon",
    evening: "enterprise.reliability.assistant.greetingEvening",
  } as const;
  const greeting = t(greetingKeyByPeriod[getDayPeriod()], {
    name: greetingName,
  });
  const signalSummary = customerSummary
    ? t("enterprise.reliability.assistant.signalSummary", {
        criticals: t("enterprise.reliability.assistant.criticalCount", {
          count: actionRequiredCount,
          formattedCount: formatCount(actionRequiredCount),
        }),
        watches: t("enterprise.reliability.assistant.watchSignalCount", {
          count: watchCount,
          formattedCount: formatCount(watchCount),
        }),
      })
    : t("enterprise.reliability.assistant.loadingSignals");
  function handleNewConversation() {
    const nextConversationId = createConversationId();
    activeConversationIdRef.current = nextConversationId;
    setActiveConversationId(nextConversationId);
    setMessages([WELCOME_MESSAGE]);
    setInput("");
    setAttachedContexts([]);
    setIsAttachOpen(false);
    setError(null);
    setShowMobileHistory(false);
  }

  function handleSelectConversation(conversation: AskReliConversationRecord) {
    activeConversationIdRef.current = conversation.id;
    setActiveConversationId(conversation.id);
    setMessages(
      localizeWelcomeMessage(
        conversation.messages,
        welcomeMessageContentRef.current,
      ),
    );
    setInput("");
    setAttachedContexts([]);
    setIsAttachOpen(false);
    setError(null);
    setShowMobileHistory(false);
  }

  function renderRecentRail() {
    return (
      <aside
        className={styles.recentRail}
        aria-label={t(
          "enterprise.reliability.assistant.conversationsAriaLabel",
        )}
      >
        <div className={styles.recentRailHeader}>
          <AskReliSparklesMark />
          <div className="min-w-0">
            <div className={styles.recentRailTitle}>
              {t("enterprise.reliability.assistant.title")}
            </div>
            <div className={styles.recentRailMeta}>
              {t("enterprise.reliability.assistant.conversationCount", {
                count: conversations.length,
              })}
            </div>
          </div>
        </div>

        <button
          type="button"
          className={styles.recentNewButton}
          onClick={handleNewConversation}
        >
          <Icon name="plus-regular" size="xs" tone="inherit" aria-hidden />
          {t("enterprise.reliability.assistant.newChat")}
        </button>

        <div className={styles.recentSectionLabel}>
          {t("enterprise.reliability.assistant.recentSectionLabel")}
        </div>
        <div className={styles.recentList}>
          {conversations.length > 0 ? (
            conversations.map((conversation) => (
              <button
                key={conversation.id}
                type="button"
                className={`${styles.recentItem} ${
                  conversation.id === activeConversationId
                    ? styles.recentItemActive
                    : ""
                }`}
                onClick={() => handleSelectConversation(conversation)}
              >
                <span className="truncate font-(family-name:--font-family-title) text-(length:--font-size-title-sm) font-(number:--font-weight-semibold) leading-(--font-line-height-20) text-text-default">
                  {formatConversationPreview(conversation.title)}
                </span>
                <span className="text-(length:--font-size-label-sm) leading-(--font-line-height-16) text-text-subtle">
                  {conversation.isLoading
                    ? t("enterprise.reliability.assistant.workingNow")
                    : formatConversationTimestamp(t, conversation.updatedAt)}
                </span>
              </button>
            ))
          ) : (
            <div className={styles.recentEmpty}>
              {t("enterprise.reliability.assistant.recentEmpty")}
            </div>
          )}
        </div>
      </aside>
    );
  }

  function renderMessageList(className: string) {
    return (
      <div className={className} data-testid="ask-reli-messages">
        {messages.map((msg, i) => (
          <div
            key={msg.id}
            className={
              msg.role === "user"
                ? `${styles.bubble} ${styles.bubbleUser}`
                : `${styles.bubble} ${styles.bubbleAssistant}`
            }
            data-testid={
              i === 0
                ? "ask-reli-welcome-message"
                : msg.role === "user"
                  ? "ask-reli-bubble-user"
                  : "ask-reli-bubble-assistant"
            }
          >
            <div
              className={
                msg.role === "assistant"
                  ? `${styles.bubbleAvatar} ${styles.bubbleAvatarAssistant}`
                  : styles.bubbleAvatar
              }
            >
              {msg.role === "user" ? (
                <Icon name="user" size="sm" tone="inherit" aria-hidden />
              ) : (
                <Icon name="sparkles" size="sm" tone="inherit" aria-hidden />
              )}
            </div>

            <div
              className={`${styles.bubbleBody} ${
                msg.role === "user" ? styles.bubbleBodyUser : ""
              }`}
            >
              <div
                className={`${styles.bubbleContent} ${
                  msg.role === "user"
                    ? styles.bubbleContentUser
                    : styles.bubbleContentAssistant
                }`}
              >
                {msg.role === "assistant" ? (
                  <MarkdownLiteContent content={msg.content} />
                ) : (
                  <Text size="sm" as="div">
                    {msg.content}
                  </Text>
                )}
                {msg.contexts && msg.contexts.length > 0 ? (
                  <div className="mt-(--spacing-6) flex flex-wrap gap-(--spacing-4)">
                    {msg.contexts.map((context) => (
                      <span
                        key={context}
                        className="inline-flex rounded-(--radius-sm) bg-surface/[0.18] px-(--spacing-6) py-(--spacing-2) font-(family-name:--font-family-label) text-(length:--font-size-label-sm) leading-(--font-line-height-16)"
                      >
                        {context}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {msg.role === "assistant" && msg.visualization?.type === "bar" ? (
                <VisualizationBarChart visualization={msg.visualization} />
              ) : null}

              {msg.role === "assistant" && msg.sql && (
                <div className={styles.sqlSection}>
                  <button
                    type="button"
                    className={styles.sqlToggle}
                    onClick={() => toggleSql(i)}
                    aria-expanded={msg.sqlExpanded}
                    data-testid="ask-reli-sql-toggle"
                  >
                    <Icon name="api" size="xs" tone="inherit" aria-hidden />
                    <span>
                      {msg.sqlExpanded
                        ? t("enterprise.reliability.assistant.hideSql")
                        : t("enterprise.reliability.assistant.showSql")}
                      {msg.rowCount !== null
                        ? ` · ${t("enterprise.reliability.assistant.rowCount", { count: msg.rowCount })}`
                        : ""}
                    </span>
                  </button>
                  {msg.sqlExpanded && (
                    <pre
                      className={styles.sqlCode}
                      data-testid="ask-reli-sql-code"
                    >
                      {msg.sql}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {activeIsLoading && (
          <div
            className={`${styles.bubble} ${styles.bubbleAssistant}`}
            data-testid="ask-reli-loading"
          >
            <div
              className={`${styles.bubbleAvatar} ${styles.bubbleAvatarAssistant}`}
            >
              <Icon name="sparkles" size="sm" tone="inherit" aria-hidden />
            </div>
            <div className={styles.bubbleBody}>
              <div className={styles.loadingDots}>
                <span className={styles.loadingDot} />
                <span
                  className={`${styles.loadingDot} ${styles.loadingDotDelay1}`}
                />
                <span
                  className={`${styles.loadingDot} ${styles.loadingDotDelay2}`}
                />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    );
  }

  function renderComposer(floating: boolean) {
    return (
      <div className={floating ? styles.floatingInputShell : ""}>
        {floating && attachedContexts.length > 0 ? (
          <div className={styles.contextChips}>
            {attachedContexts.map((context) => (
              <span className={styles.contextChip} key={context.id}>
                <span className="truncate">{context.label}</span>
                <button
                  type="button"
                  className={styles.contextChipRemove}
                  aria-label={t(
                    "enterprise.reliability.assistant.removeContextAria",
                    { label: context.label },
                  )}
                  onClick={() =>
                    setAttachedContexts((current) =>
                      current.filter((item) => item.id !== context.id),
                    )
                  }
                >
                  <Icon name="x-close" size="xs" tone="inherit" aria-hidden />
                </button>
              </span>
            ))}
          </div>
        ) : null}

        {floating && isAttachOpen ? (
          <div className={styles.attachPopover}>
            <div className={styles.attachPopoverHeader}>
              {t("enterprise.reliability.assistant.attachFacilityHeader")}
            </div>
            <div className={styles.attachPopoverList}>
              {attachmentOptions.length > 0 ? (
                attachmentOptions.map((context) => {
                  const isAttached = attachedContexts.some(
                    (item) => item.id === context.id,
                  );

                  return (
                    <button
                      key={context.id}
                      type="button"
                      className={styles.attachPopoverRow}
                      onClick={() => {
                        setAttachedContexts((current) =>
                          current.some((item) => item.id === context.id)
                            ? current
                            : [...current, context],
                        );
                        setIsAttachOpen(false);
                      }}
                      disabled={isAttached}
                    >
                      <span className={styles.attachPopoverDot} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-(family-name:--font-family-title) text-(length:--font-size-title-sm) font-(number:--font-weight-medium) leading-(--font-line-height-20) text-text-default">
                          {context.label}
                        </span>
                        <span className="block truncate text-(length:--font-size-label-sm) leading-(--font-line-height-16) text-text-subtle">
                          {isAttached
                            ? t("enterprise.reliability.assistant.attached")
                            : context.description}
                        </span>
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="px-(--spacing-12) py-(--spacing-10) text-(length:--font-size-body-md) text-text-subtle">
                  {t("enterprise.reliability.assistant.noFacilities")}
                </div>
              )}
            </div>
          </div>
        ) : null}

        <div className={floating ? styles.floatingInputRow : styles.inputRow}>
          {floating ? (
            <button
              type="button"
              className={`${styles.iconButton} ${
                isAttachOpen ? styles.iconButtonActive : ""
              }`}
              aria-label={t(
                "enterprise.reliability.assistant.attachContextAria",
              )}
              aria-expanded={isAttachOpen}
              onClick={() => setIsAttachOpen((open) => !open)}
              disabled={activeIsLoading}
            >
              <Icon name="asset" size="sm" tone="inherit" aria-hidden />
            </button>
          ) : null}
          <textarea
            ref={textareaRef}
            className={floating ? styles.floatingTextarea : styles.textarea}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              floating
                ? t("enterprise.reliability.assistant.floatingPlaceholder")
                : t("enterprise.reliability.assistant.inputPlaceholder")
            }
            rows={floating ? 1 : 2}
            disabled={activeIsLoading}
            aria-label={t("enterprise.reliability.assistant.inputAriaLabel")}
            data-testid="ask-reli-input"
          />
          <button
            type="button"
            className={styles.sendButton}
            onClick={() => void handleSend()}
            disabled={!input.trim() || activeIsLoading}
            aria-label={t("enterprise.reliability.assistant.sendAriaLabel")}
            data-testid="ask-reli-send"
          >
            <Icon name="paper-plane-top" size="sm" tone="inherit" aria-hidden />
          </button>
        </div>

        <div className={floating ? styles.floatingFooter : styles.footer}>
          <span className={styles.footerIcon}>
            <Icon name="sparkles" size="sm" tone="inherit" aria-hidden />
          </span>
          <span>{t("enterprise.reliability.assistant.footerNote")}</span>
        </div>
      </div>
    );
  }

  function renderRecentToggleButton(tone: "hero" | "surface" = "hero") {
    const recentToggleClassName =
      tone === "hero"
        ? `${styles.heroIconButton} relative ${
            showMobileHistory ? styles.heroIconButtonActive : ""
          } ${isExpanded ? "sm:hidden" : ""}`
        : `${styles.surfaceIconButton} ${
            showMobileHistory ? styles.surfaceIconButtonActive : ""
          } ${isExpanded ? "sm:hidden" : ""}`;

    return (
      <button
        type="button"
        className={recentToggleClassName}
        onClick={() => {
          setShowMobileHistory((showHistory) => !showHistory);
        }}
        aria-expanded={showMobileHistory}
        aria-label={
          showMobileHistory
            ? t("enterprise.reliability.assistant.hideRecent")
            : t("enterprise.reliability.assistant.showRecent")
        }
      >
        <Icon name="clock-seven" size="sm" tone="inherit" aria-hidden />
        {conversations.length > 0 ? (
          <span className={styles.recentToggleBadge}>
            {Math.min(conversations.length, 9)}
          </span>
        ) : null}
      </button>
    );
  }

  if (isFloating) {
    return (
      <div
        className={isExpanded ? styles.expandedPanel : styles.floatingPanel}
        data-testid="ask-reli-panel"
      >
        {isExpanded ? renderRecentRail() : null}
        <div className={styles.expandedMain}>
          {!hasActiveConversation || showMobileHistory ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <section
                className={
                  isExpanded ? styles.expandedHero : styles.floatingHero
                }
              >
                <div
                  className={
                    isExpanded
                      ? styles.expandedHeroHead
                      : styles.floatingHeroHead
                  }
                >
                  {isExpanded ? (
                    <>
                      <div className={`${styles.heroDate} max-sm:hidden`}>
                        {formatHeaderDate()}
                      </div>
                      <div className="hidden min-w-0 items-center gap-(--spacing-10) max-sm:flex">
                        <AskReliSparklesMark className="text-[var(--enterprise-ask-reli-hero-foreground)]" />
                        <div className="min-w-0">
                          <div
                            className={styles.heroKicker}
                            data-testid="ask-reli-heading"
                          >
                            {t("enterprise.reliability.assistant.title")}
                          </div>
                          <div className={styles.heroDate}>
                            {formatHeaderDate()}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <AskReliSparklesMark className="text-[var(--enterprise-ask-reli-hero-foreground)]" />
                      <div className="min-w-0">
                        <div
                          className={styles.heroKicker}
                          data-testid="ask-reli-heading"
                        >
                          {t("enterprise.reliability.assistant.title")}
                        </div>
                        <div className={styles.heroDate}>
                          {formatHeaderDate()}
                        </div>
                      </div>
                    </>
                  )}
                  <div className={styles.floatingHeroActions}>
                    {renderRecentToggleButton()}
                    {onToggleExpand ? (
                      <button
                        type="button"
                        className={styles.heroIconButton}
                        aria-label={
                          isExpanded
                            ? t("enterprise.reliability.assistant.shrinkChat")
                            : t("enterprise.reliability.assistant.expandChat")
                        }
                        onClick={onToggleExpand}
                      >
                        <Icon
                          name="expand"
                          size="sm"
                          tone="inherit"
                          aria-hidden
                        />
                      </button>
                    ) : null}
                    {onClose ? (
                      <button
                        type="button"
                        className={styles.heroIconButton}
                        aria-label={t(
                          "enterprise.reliability.assistant.closeChat",
                        )}
                        onClick={onClose}
                      >
                        <Icon
                          name="x-close"
                          size="sm"
                          tone="inherit"
                          aria-hidden
                        />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div
                  className={
                    isExpanded
                      ? styles.expandedHeroGreeting
                      : styles.heroGreeting
                  }
                >
                  {greeting}
                  <br />
                  <span className="text-[var(--enterprise-ask-reli-hero-accent)]">
                    {signalSummary}
                  </span>
                </div>
              </section>

              <div
                className={
                  isExpanded
                    ? showMobileHistory
                      ? styles.expandedTemplateSectionScrollable
                      : styles.expandedTemplateSection
                    : showMobileHistory
                      ? styles.templateSectionScrollable
                      : styles.templateSection
                }
                data-testid="ask-reli-suggestions"
              >
                {showMobileHistory ? (
                  <>
                    <button
                      type="button"
                      className={styles.mobileRecentAction}
                      aria-label={t(
                        "enterprise.reliability.assistant.newConversationAria",
                      )}
                      onClick={handleNewConversation}
                    >
                      <Icon
                        name="plus-regular"
                        size="sm"
                        tone="inherit"
                        aria-hidden
                      />
                      {t("enterprise.reliability.assistant.newChat")}
                    </button>
                    {conversations.map((conversation) => (
                      <button
                        key={conversation.id}
                        type="button"
                        className={`${styles.mobileRecentItem} ${
                          conversation.id === activeConversationId
                            ? styles.recentItemActive
                            : ""
                        }`}
                        onClick={() => handleSelectConversation(conversation)}
                      >
                        <span className="truncate font-(family-name:--font-family-title) text-(length:--font-size-title-sm) font-(number:--font-weight-semibold) leading-(--font-line-height-20) text-text-default">
                          {formatConversationPreview(conversation.title)}
                        </span>
                        <span className="text-(length:--font-size-label-sm) leading-(--font-line-height-16) text-text-subtle">
                          {conversation.isLoading
                            ? t("enterprise.reliability.assistant.workingNow")
                            : formatConversationTimestamp(
                                t,
                                conversation.updatedAt,
                              )}
                        </span>
                      </button>
                    ))}
                  </>
                ) : (
                  TEMPLATE_QUESTIONS.map((question) => (
                    <button
                      key={question.prompt}
                      type="button"
                      className={
                        isExpanded
                          ? styles.expandedSuggestionTile
                          : styles.suggestionTile
                      }
                      data-testid="ask-reli-suggestion-chip"
                      onClick={() => void handleSend(question.prompt)}
                      disabled={activeIsLoading}
                    >
                      <span className={styles.suggestionIcon}>
                        <Icon
                          name={question.icon}
                          size="sm"
                          tone="inherit"
                          aria-hidden
                        />
                      </span>
                      <span className="font-(family-name:--font-family-title) text-(length:--font-size-title-sm) font-(number:--font-weight-medium) leading-(--font-line-height-20) text-text-default">
                        {question.title}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-(--spacing-10) border-b border-outline-subtle bg-surface px-(--spacing-16) py-(--spacing-12)">
                {isExpanded ? (
                  <AskReliSparklesMark className="hidden max-sm:flex" />
                ) : (
                  <AskReliSparklesMark />
                )}
                <div className="min-w-0" data-testid="ask-reli-heading">
                  {isExpanded ? (
                    <div className="hidden truncate font-(family-name:--font-family-title) text-(length:--font-size-title-sm) font-(number:--font-weight-semibold) leading-(--font-line-height-24) text-text-default max-sm:block">
                      {t("enterprise.reliability.assistant.title")}
                    </div>
                  ) : (
                    <div className="truncate font-(family-name:--font-family-title) text-(length:--font-size-title-sm) font-(number:--font-weight-semibold) leading-(--font-line-height-24) text-text-default">
                      {t("enterprise.reliability.assistant.title")}
                    </div>
                  )}
                  <div className="font-(family-name:--font-family-label) text-(length:--font-size-label-md) font-(number:--font-weight-medium) leading-(--font-line-height-16) text-text-subtle">
                    {t("enterprise.reliability.assistant.messageCount", {
                      count: messages.length,
                    })}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-(--spacing-8)">
                  {!isExpanded ? renderRecentToggleButton("surface") : null}
                  {onToggleExpand ? (
                    <IconButton
                      icon="expand"
                      variant="ghost"
                      size="sm"
                      aria-label={
                        isExpanded
                          ? t("enterprise.reliability.assistant.shrinkChat")
                          : t("enterprise.reliability.assistant.expandChat")
                      }
                      onClick={onToggleExpand}
                    />
                  ) : null}
                  {onClose ? (
                    <IconButton
                      icon="x-close"
                      variant="ghost"
                      size="sm"
                      aria-label={t(
                        "enterprise.reliability.assistant.closeChat",
                      )}
                      onClick={onClose}
                    />
                  ) : null}
                </div>
              </div>
              {renderMessageList(styles.floatingMessages)}
            </>
          )}

          {error ? (
            <div className={styles.floatingError} data-testid="ask-reli-error">
              <Text size="sm" tone="subtle">
                {error}
              </Text>
            </div>
          ) : null}

          {renderComposer(true)}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel} data-testid="ask-reli-panel">
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <Icon name="sparkles" size="xl" tone="inherit" aria-hidden />
        </div>
        <div data-testid="ask-reli-heading">
          <Title size="lg" as="h2">
            {t("enterprise.reliability.assistant.title")}
          </Title>
          <Text size="sm" tone="subtle">
            {t("enterprise.reliability.assistant.subtitle")}
          </Text>
        </div>
      </div>

      <div
        className="flex flex-wrap gap-(--spacing-8)"
        data-testid="ask-reli-suggestions"
      >
        {TEMPLATE_QUESTIONS.map((q) => (
          <button
            key={q.prompt}
            type="button"
            className={styles.statusChip}
            data-testid="ask-reli-suggestion-chip"
            onClick={() => void handleSend(q.prompt)}
            disabled={activeIsLoading}
          >
            {q.title}
          </button>
        ))}
      </div>

      <div className={styles.messagesCard}>
        {renderMessageList(styles.messages)}
      </div>

      {error && (
        <div className={styles.error} data-testid="ask-reli-error">
          <Text size="sm" tone="subtle">
            {error}
          </Text>
        </div>
      )}

      {renderComposer(false)}
    </div>
  );
}
